<template>
  <div class="ed-aging ed-aging--cobrar">
    <div class="ed-aging__card">
      <header class="ed-aging__head">
        <div>
          <p class="ed-aging__eyebrow">Cuentas por cobrar</p>
          <h3 class="ed-aging__title">Antigüedad por cobrar</h3>
        </div>
        <div class="ed-aging__total" v-if="!loading">
          <span class="ed-aging__total-label">Total</span>
          <span class="ed-aging__total-value">{{ formattedTotal }}</span>
        </div>
      </header>

      <div v-if="!loading" class="ed-aging__body">
        <!-- Health bar: shows % current vs aged -->
        <div class="ed-aging__health" v-if="cxc.series.length > 0">
          <div class="ed-aging__health-track">
            <span
              v-for="(seg, i) in segments"
              :key="i"
              class="ed-aging__health-seg"
              :style="{ width: seg.pct + '%', background: seg.color }"
              :title="seg.label + ': ' + seg.pct.toFixed(1) + '%'"
            ></span>
          </div>
          <div class="ed-aging__health-meta">
            <span><strong>{{ healthPct.toFixed(1) }}%</strong> al día</span>
            <span class="ed-aging__health-sep">·</span>
            <span><strong>{{ (100 - healthPct).toFixed(1) }}%</strong> vencido</span>
          </div>
        </div>

        <ul class="ed-aging__list" v-if="cxc.series.length > 0">
          <li v-for="(b, i) in buckets" :key="i" class="ed-aging__row" :class="`is-${b.tone}`">
            <span class="ed-aging__row-pip" :style="{ background: b.color }"></span>
            <span class="ed-aging__row-label">{{ b.label }}</span>
            <span class="ed-aging__row-bar">
              <span :style="{ width: b.pct + '%', background: b.color }"></span>
            </span>
            <span class="ed-aging__row-pct">{{ b.pct.toFixed(1) }}%</span>
            <span class="ed-aging__row-value">{{ fmt(b.value) }}</span>
          </li>
        </ul>

        <div v-else class="ed-aging__empty">Sin saldos pendientes por cobrar.</div>
      </div>

      <div v-else class="ed-aging__skeleton">
        <div v-for="n in 4" :key="n" class="ed-aging__sk-row"></div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "AccountsReceivableDashboard",
  props: {
    filter: { type: Object, required: true },
  },
  data() {
    return {
      cxc: { series: [], label: [] },
      loading: true,
    };
  },
  computed: {
    total() {
      return this.cxc.series.reduce((sum, v) => sum + parseFloat(v || 0), 0);
    },
    formattedTotal() {
      const cc = this.getCurrencyCode();
      return `${this.fmt(this.total)} ${cc}`;
    },
    palette() {
      // green → amber → red, matching the editorial sage/brass/claret family
      return ["#4F6F5C", "#7E9B6B", "#B68A3E", "#C46A3A", "#9A3334", "#7B1D1E"];
    },
    buckets() {
      if (!this.cxc.series.length) return [];
      const total = this.total || 1;
      return this.cxc.label.map((label, i) => {
        const value = parseFloat(this.cxc.series[i] || 0);
        const pct = (value / total) * 100;
        const color = this.palette[Math.min(i, this.palette.length - 1)];
        const tone = i === 0 ? "ok" : i < 2 ? "warn" : "bad";
        return { label, value, pct, color, tone };
      });
    },
    segments() {
      return this.buckets;
    },
    healthPct() {
      // first bucket is "current" / al día
      return this.buckets.length ? this.buckets[0].pct : 0;
    },
  },
  watch: { filter: { handler: "getData", deep: true } },
  created() { this.getData(); },
  methods: {
    fmt(n) {
      return this.currencyFormat
        ? this.currencyFormat(n)
        : Number(n).toLocaleString("en-US", { maximumFractionDigits: 2 });
    },
    getCurrencyCode() {
      const bu = this.$store.state.auth.bussines_units?.find(b => b.value === this.filter.business);
      return bu ? bu.currency_code : "";
    },
    async getData() {
      this.loading = true;
      try {
        const payload = {
          business_unit_id: this.filter.business,
          location: this.filter.location,
          type: 2,
        };
        const data = await this.$store.dispatch("dashboard/fetchCached", {
          endpoint: "transactions/dashboard_billing",
          payload,
          fetcher: async () => {
            const r = await this.api_Post("transactions/dashboard_billing", payload);
            if (!r.ok) throw new Error("API request failed");
            return await r.json();
          },
        });
        this.processData(data);
      } catch (error) {
        console.error("Error fetching data:", error);
        this.$toast?.error?.("Error al cargar los datos");
      } finally {
        this.loading = false;
      }
    },
    processData(data) {
      this.cxc = (data || []).reduce(
        (acc, item) => {
          acc.label.push(item.rango);
          acc.series.push(parseFloat(item.total_pending_amount));
          return acc;
        },
        { label: [], series: [] }
      );
    },
  },
};
</script>

<style lang="scss" scoped>
$ink:    #2A3547;
$muted:  #5A6A85;
$muted-soft: #8A98AB;
$rule:   #E5EAF2;
$rule-soft: #EEF1F6;
$bg:     #F7F9FC;
$card:   #FFFFFF;
$primary: #00897B;
$primary-soft: #E0F2F1;
$info: #2196F3;
$info-soft: #E3F2FD;
$accent: #5C6BC0;
$accent-soft: #E8EAF6;
$warning: #F57F17;
$warning-soft: #FFF3E0;
$error: #E53935;
$error-soft: rgba(229, 57, 53, .1);
$success: #2E7D32;
$shadow: rgb(145 158 171 / 20%) 0px 0px 2px 0px, rgb(145 158 171 / 12%) 0px 8px 18px -6px;

$font: 'Plus Jakarta Sans', sans-serif;

.ed-aging { font-family: $font; color: $ink; }
.ed-aging__card {
  background: $card; border: 1px solid $rule; padding: 26px 28px;
  border-radius: 14px; box-shadow: $shadow;
}

.ed-aging__head {
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 16px; padding-bottom: 18px;
  border-bottom: 1px solid $rule;
}
.ed-aging__eyebrow { font-family: $font; font-size: 10px; font-weight: 700; letter-spacing: .22em; text-transform: uppercase; color: $muted; margin: 0 0 6px; }
.ed-aging__title  { font-family: $font; font-size: 24px; font-weight: 700; letter-spacing: -.02em; margin: 0; color: $ink; }

.ed-aging__total { text-align: right; }
.ed-aging__total-label { display: block; font-family: $font; font-size: 10px; font-weight: 700; letter-spacing: .22em; text-transform: uppercase; color: $muted; }
.ed-aging__total-value { font-family: $font; font-size: 28px; font-weight: 800; letter-spacing: -.02em; color: $primary; }

.ed-aging--cobrar .ed-aging__total-value { color: $primary; }
.ed-aging--pagar  .ed-aging__total-value { color: $error; }

.ed-aging__body { padding-top: 18px; display: flex; flex-direction: column; gap: 20px; }

// Health bar
.ed-aging__health-track {
  display: flex; height: 8px;
  background: $rule-soft; overflow: hidden;
  border-radius: 999px;
}
.ed-aging__health-seg { display: block; height: 100%; transition: width .5s cubic-bezier(.2,.8,.2,1); }
.ed-aging__health-meta {
  display: flex; gap: 8px; align-items: baseline;
  margin-top: 8px; font-family: $font; font-size: 12px; color: $muted;
}
.ed-aging__health-meta strong { font-family: $font; font-weight: 800; color: $ink; }
.ed-aging__health-sep { color: $muted-soft; }

// Aging rows
.ed-aging__list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 14px; }
.ed-aging__row {
  display: grid;
  grid-template-columns: 8px 1.3fr 2fr 60px auto;
  align-items: center; gap: 14px;
  padding-bottom: 14px;
  border-bottom: 1px solid $rule-soft;
}
.ed-aging__row:last-child { border-bottom: 0; padding-bottom: 0; }
.ed-aging__row-pip { width: 8px; height: 8px; border-radius: 999px; }
.ed-aging__row-label { font-family: $font; font-size: 15px; font-weight: 600; color: $ink; letter-spacing: -.01em; }
.ed-aging__row-bar {
  display: block; position: relative; height: 6px; background: $rule-soft;
  border-radius: 999px; overflow: hidden;
}
.ed-aging__row-bar span {
  position: absolute; left: 0; top: 0; bottom: 0;
  border-radius: 999px;
  animation: grow .6s cubic-bezier(.2,.8,.2,1);
}
@keyframes grow { from { width: 0 !important; } }
.ed-aging__row-pct { font-family: $font; font-size: 12px; font-weight: 600; color: $muted; text-align: right; }
.ed-aging__row-value { font-family: $font; font-size: 13px; font-weight: 700; color: $ink; text-align: right; }

.ed-aging__empty { padding: 30px 0; text-align: center; font-family: $font; color: $muted; }

.ed-aging__skeleton { display: flex; flex-direction: column; gap: 14px; padding-top: 10px; }
.ed-aging__sk-row {
  height: 24px;
  background: linear-gradient(90deg, $bg 0%, $rule-soft 50%, $bg 100%);
  background-size: 200% 100%;
  animation: shimmer 1.4s linear infinite;
  border-radius: 14px;
}
@keyframes shimmer { 0%{background-position:200% 0;} 100%{background-position:-200% 0;} }

@media (max-width: 720px) {
  .ed-aging__head { flex-direction: column; align-items: flex-start; }
  .ed-aging__total { text-align: left; }
  .ed-aging__row { grid-template-columns: 8px 1fr 1fr 50px auto; gap: 8px; }
}
</style>
